import './InsertQuestion.scss';

import { useRef } from 'react';
import * as React from 'react';

import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import cx from 'classnames';
import useWhatInput from 'react-use-what-input';

import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { IconPlus } from '~shared/components/icons/standard';

interface Props {
  onInsertQuestion: (onClickArguments: {
    questionType: QuestionTypes;
    position: number;
  }) => void;
  position: number;
  openGenerateQuestionDialog: (position: number) => void;
  openExtractQuestionsDialog: (position: number) => void;
  openImportByTextDialog: (position: number) => void;
}

const InsertQuestion = ({
  position,
  onInsertQuestion,
  openGenerateQuestionDialog,
  openExtractQuestionsDialog,
  openImportByTextDialog,
}: Props) => {
  const inputIntent = useWhatInput('intent');

  const [showQuestionOptions, setShowQuestionOptions] = React.useState(false);
  const insertQuestionRef = React.useRef<HTMLDivElement>();
  const addQuestionClasses = cx('transition ease-linear duration-100', {
    'opacity-0 focus-within:opacity-100 hover:opacity-100':
      inputIntent !== 'touch' && !showQuestionOptions,
    'opacity-0': showQuestionOptions,
  });

  const handleClickOutside = (event) => {
    const { current } = insertQuestionRef;
    if (current && !current.contains(event.target)) {
      setShowQuestionOptions(false);
      document.removeEventListener('click', handleClickOutside, true);
    }
  };

  const handleAddQuestionClicked = () => {
    setShowQuestionOptions(true);
    if (inputIntent === 'keyboard') {
      shouldFocusOnFirstButton.current = true;
    }
    document.addEventListener('click', handleClickOutside, true);
  };

  const handleInsertQuestion = (questionType: QuestionTypes) => {
    setShowQuestionOptions(false);
    onInsertQuestion({ position, questionType });
  };

  const shouldFocusOnFirstButton = useRef<boolean>(false);

  const focusOnFirstButton = (button: HTMLButtonElement) => {
    if (shouldFocusOnFirstButton.current === true) {
      shouldFocusOnFirstButton.current = false;
      button.focus();
    }
  };

  return (
    <div className="relative insert-question" data-insert-question>
      <div className="absolute z-100 w-full h-4 flex flex-col sm:flex-row justify-center items-center insert-question__wrapper">
        <div className={addQuestionClasses}>
          <div className="m-2 text-white rounded-2">
            <SmallButton
              onClick={handleAddQuestionClicked}
              appearance="default"
            >
              <span className="inline-flex mr-1 -ml-2 text-inherit">
                <IconPlus />
              </span>
              {translate('Add Question')}
            </SmallButton>
          </div>
        </div>
        {showQuestionOptions && (
          <div
            className="absolute top-1/2 left-1/2 px-2 flex flex-wrap justify-center items-center insert-question__types"
            ref={insertQuestionRef}
          >
            <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
              <SmallButton
                ref={focusOnFirstButton}
                appearance="default"
                onClick={() =>
                  handleInsertQuestion(QuestionTypes.MultipleChoice)
                }
                isDisabled={!showQuestionOptions}
              >
                {translate('Multiple Choice')}
              </SmallButton>
            </div>
            <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
              <SmallButton
                appearance="default"
                onClick={() => handleInsertQuestion(QuestionTypes.TrueFalse)}
                isDisabled={!showQuestionOptions}
              >
                {translate('True / False')}
              </SmallButton>
            </div>
            <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
              <SmallButton
                onClick={() => handleInsertQuestion(QuestionTypes.FreeResponse)}
                appearance="default"
                isDisabled={!showQuestionOptions}
              >
                {translate('Short Answer')}
              </SmallButton>
            </div>
            <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
              <SmallButton
                onClick={() => openGenerateQuestionDialog(position)}
                appearance="primary"
                isDisabled={!showQuestionOptions}
              >
                {translate('Generate Questions')}
              </SmallButton>
            </div>
            <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
              <SmallButton
                onClick={() => openExtractQuestionsDialog(position)}
                appearance="primary"
                isDisabled={!showQuestionOptions}
              >
                {translate('Extract Questions from Document')}
              </SmallButton>
            </div>
            <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
              <SmallButton
                onClick={() => openImportByTextDialog(position)}
                appearance="primary"
                isDisabled={!showQuestionOptions}
              >
                {translate('Copy-Paste Questions')}
              </SmallButton>
            </div>
            {/*
              <div className="relative z-0 focus-within:z-10 slide-up m-2 text-white rounded-2">
                <SmallButton
                  className="bg-orange-50 text-orange-600 hover:bg-orange-100 focus:bg-orange-100 hover:text-orange-700 focus:text-orange-700"
                  onClick={() => handleInsertQuestion(QuestionTypes.Matching)}
                  appearance={null}
                  isDisabled={!showQuestionOptions}
                >
                  {'Matching'}
                </SmallButton>
              </div>
            ) : null}
            */}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(InsertQuestion);
